import { ApplicationRef, DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';
import { concat, from, interval, of, switchMap, timer } from 'rxjs';
import { DialogService } from './dialog.service';
import { devLog } from '../shared/dev-log';
import { AuthService } from './auth/auth.service';

export const FIVE_MINUTES = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly dialogService = inject(DialogService);
  private readonly appRef = inject(ApplicationRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private updateFlowStarted = false;

  init(): void {
    if (!this.swUpdate.isEnabled) return;

    // Új verzió letöltve és készen áll az aktiválásra
    this.swUpdate.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(e => {
      devLog('SW', e.type);
      if (e.type === 'VERSION_READY') {
        this.startUpdateFlow();
      }
    });

    // Stabilizálódáskor, majd 5 percenként keres
    concat(from(this.appRef.whenStable()), interval(FIVE_MINUTES))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.swUpdate.checkForUpdate().catch(() => {})); // offline: nem hiba
  }

  private startUpdateFlow(): void {
    if (this.updateFlowStarted) return;
    this.updateFlowStarted = true;

    if (!this.authService.isSignedIn()) {
      this.reload();
      return;
    }

    this.dialogService
      .openUpdateAvailableDialog(true)
      .pipe(
        switchMap(refreshNow =>
          refreshNow
            ? of(true)
            : timer(FIVE_MINUTES).pipe(switchMap(() => this.dialogService.openUpdateAvailableDialog(false))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.reload());
  }

  private reload(): void {
    document.location.reload();
  }
}
